<template>
  <div v-if="completionPercentage < 100"
    class="rounded-2xl border p-6 mb-6 transition-all duration-300"
    :class="isDark
      ? 'bg-gradient-to-r from-zinc-900/40 to-zinc-900/20 border-zinc-800/60'
      : 'bg-gradient-to-r from-zinc-50 to-white border-zinc-200/60'">

    <div class="flex items-start justify-between mb-4">
      <div class="text-left">
        <h3 class="text-lg font-medium mb-1 transition-colors duration-200"
          :class="isDark ? 'text-white' : 'text-zinc-900'">
          Complétez votre profil professionnel
        </h3>
        <p class="text-xs transition-colors duration-200"
          :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
          {{ completedSteps }}/{{ totalSteps }} étapes complétées pour rendre votre fiche visible au public
        </p>
      </div>

      <div class="flex items-center gap-3">

      
        <div class="text-right">
          <div class="text-2xl font-bold bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">
            {{ completionPercentage }}%
          </div>
          <div class="text-[10px] uppercase tracking-widest mt-0.5 transition-colors duration-200"
            :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
            Complété
          </div>
        </div>
      </div>
    </div>

    <!-- Progress Bar -->
    <div class="w-full h-2 rounded-full mb-5 overflow-hidden transition-colors duration-200"
      :class="isDark ? 'bg-zinc-900' : 'bg-zinc-200'">
      <div
        class="h-2 bg-emerald-500 to-teal-500 transition-all duration-500 ease-out rounded-full"
        :style="{ width: completionPercentage + '%' }">
      </div>
    </div>

    <!-- Steps Grid -->
    <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">

      <!-- Step 1: Services -->
      <div @click="navigateTo('/services')" class="flex items-start gap-3 p-3 rounded-xl transition-all duration-200 cursor-pointer"
        :class="[
          steps.hasServices ? 'opacity-60' : '',
          isDark
            ? 'bg-zinc-900/40 hover:bg-zinc-900/60'
            : 'bg-zinc-50 hover:bg-zinc-100/80'
        ]">
        <div class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-all duration-200"
          :class="steps.hasServices
            ? 'bg-emerald-500/10 text-emerald-500'
            : isDark
              ? 'bg-zinc-800 text-zinc-600'
              : 'bg-zinc-200 text-zinc-400'">
          <svg v-if="steps.hasServices" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
          </svg>
        </div>
        <div class="flex-1 text-left">
          <div class="text-sm font-medium mb-0.5 transition-colors duration-200"
            :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
            Ajouter des services
          </div>
          <div class="text-[10px] transition-colors duration-200"
            :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
            {{ steps.hasServices ? '✓ Complété' : 'Catalogue de prestations' }}
          </div>
        </div>
      </div>

      <!-- Step 2: Horaires -->
      <div @click="navigateTo('/settings')" class="flex items-start gap-3 p-3 rounded-xl transition-all duration-200 cursor-pointer"
        :class="[
          steps.hasSchedule ? 'opacity-60' : '',
          isDark
            ? 'bg-zinc-900/40 hover:bg-zinc-900/60'
            : 'bg-zinc-50 hover:bg-zinc-100/80'
        ]">
        <div class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-all duration-200"
          :class="steps.hasSchedule
            ? 'bg-emerald-500/10 text-emerald-500'
            : isDark
              ? 'bg-zinc-800 text-zinc-600'
              : 'bg-zinc-200 text-zinc-400'">
          <svg v-if="steps.hasSchedule" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div class="flex-1 text-left">
          <div class="text-sm font-medium mb-0.5 transition-colors duration-200"
            :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
            Configurer les horaires
          </div>
          <div class="text-[10px] transition-colors duration-200"
            :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
            {{ steps.hasSchedule ? '✓ Complété' : 'Heures d\'ouverture' }}
          </div>
        </div>
      </div>

      <!-- Step 3: Photo -->
      <div @click="navigateTo('/settings')" class="flex items-start gap-3 p-3 rounded-xl transition-all duration-200 cursor-pointer"
        :class="[
          steps.hasPhoto ? 'opacity-60' : '',
          isDark
            ? 'bg-zinc-900/40 hover:bg-zinc-900/60'
            : 'bg-zinc-50 hover:bg-zinc-100/80'
        ]">
        <div class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-all duration-200"
          :class="steps.hasPhoto
            ? 'bg-emerald-500/10 text-emerald-500'
            : isDark
              ? 'bg-zinc-800 text-zinc-600'
              : 'bg-zinc-200 text-zinc-400'">
          <svg v-if="steps.hasPhoto" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
        </div>
        <div class="flex-1 text-left">
          <div class="text-sm font-medium mb-0.5 transition-colors duration-200"
            :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
            Ajouter une photo
          </div>
          <div class="text-[10px] transition-colors duration-200"
            :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
            {{ steps.hasPhoto ? '✓ Complété' : 'Photo de profil' }}
          </div>
        </div>
      </div>

      <!-- Step 4: Categories -->
      <div @click="navigateTo('/services')" class="flex items-start gap-3 p-3 rounded-xl transition-all duration-200 cursor-pointer"
        :class="[
          steps.hasCategories ? 'opacity-60' : '',
          isDark
            ? 'bg-zinc-900/40 hover:bg-zinc-900/60'
            : 'bg-zinc-50 hover:bg-zinc-100/80'
        ]">
        <div class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-all duration-200"
          :class="steps.hasCategories
            ? 'bg-emerald-500/10 text-emerald-500'
            : isDark
              ? 'bg-zinc-800 text-zinc-600'
              : 'bg-zinc-200 text-zinc-400'">
          <svg v-if="steps.hasCategories" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
          </svg>
        </div>
        <div class="flex-1 text-left">
          <div class="text-sm font-medium mb-0.5 transition-colors duration-200"
            :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
            Créer des catégories
          </div>
          <div class="text-[10px] transition-colors duration-200"
            :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
            {{ steps.hasCategories ? '✓ Complété' : 'Organisation services' }}
          </div>
        </div>
      </div>

      <!-- Step 5: Staff (optionnel si > 1 employé) -->
      <div v-if="nombreEmployes > 1 && accountType !== 'solo'" @click="navigateTo('/staff')" class="flex items-start gap-3 p-3 rounded-xl transition-all duration-200 cursor-pointer"
        :class="[
          steps.hasStaff ? 'opacity-60' : '',
          isDark
            ? 'bg-zinc-900/40 hover:bg-zinc-900/60'
            : 'bg-zinc-50 hover:bg-zinc-100/80'
        ]">
        <div class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-all duration-200"
          :class="steps.hasStaff
            ? 'bg-emerald-500/10 text-emerald-500'
            : isDark
              ? 'bg-zinc-800 text-zinc-600'
              : 'bg-zinc-200 text-zinc-400'">
          <svg v-if="steps.hasStaff" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
          </svg>
        </div>
        <div class="flex-1 text-left">
          <div class="text-sm font-medium mb-0.5 transition-colors duration-200"
            :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
            Ajouter l'équipe
          </div>
          <div class="text-[10px] transition-colors duration-200"
            :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
            {{ steps.hasStaff ? '✓ Complété' : 'Profils employés' }}
          </div>
        </div>
      </div>

      <!-- Step 6: Description -->
      <div @click="navigateTo('/settings')" class="flex items-start gap-3 p-3 rounded-xl transition-all duration-200 cursor-pointer"
        :class="[
          steps.hasDescription ? 'opacity-60' : '',
          isDark
            ? 'bg-zinc-900/40 hover:bg-zinc-900/60'
            : 'bg-zinc-50 hover:bg-zinc-100/80'
        ]">
        <div class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-all duration-200"
          :class="steps.hasDescription
            ? 'bg-emerald-500/10 text-emerald-500'
            : isDark
              ? 'bg-zinc-800 text-zinc-600'
              : 'bg-zinc-200 text-zinc-400'">
          <svg v-if="steps.hasDescription" class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
          </svg>
        </div>
        <div class="flex-1 text-left">
          <div class="text-sm font-medium mb-0.5 transition-colors duration-200"
            :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
            Rédiger une description
          </div>
          <div class="text-[10px] transition-colors duration-200"
            :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
            {{ steps.hasDescription ? '✓ Complété' : 'Présentation établissement' }}
          </div>
        </div>
      </div>

      <!-- Step 7: Abonnement Actif - Ne s'affiche que si pas encore souscrit -->
      <div v-if="!steps.hasActiveSubscription" @click="navigateTo('/subscription/plans')" class="flex items-start gap-3 p-3 rounded-xl transition-all duration-200 cursor-pointer"
        :class="isDark
          ? 'bg-zinc-900/40 hover:bg-zinc-900/60'
          : 'bg-zinc-50 hover:bg-zinc-100/80'">
        <div class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 transition-all duration-200"
          :class="isDark
            ? 'bg-zinc-800 text-zinc-600'
            : 'bg-zinc-200 text-zinc-400'">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
          </svg>
        </div>
        <div class="flex-1 text-left">
          <div class="text-sm font-medium mb-0.5 transition-colors duration-200"
            :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
            Abonnement actif
          </div>
          <div class="text-[10px] transition-colors duration-200"
            :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
            Souscrire à un forfait
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '@/api'
import { useTheme } from '@/composables/useTheme'

const { isDark } = useTheme()
const router = useRouter()
const route = useRoute()

const steps = ref({
  hasServices: false,
  hasSchedule: false,
  hasPhoto: false,
  hasCategories: false,
  hasStaff: false,
  hasDescription: false,
  hasActiveSubscription: false
})

const nombreEmployes = ref(1)
const accountType = ref('solo')

const completedSteps = computed(() => {
  let count = 0
  if (steps.value.hasServices) count++
  if (steps.value.hasSchedule) count++
  if (steps.value.hasPhoto) count++
  if (steps.value.hasCategories) count++
  if (steps.value.hasDescription) count++
  if (steps.value.hasActiveSubscription) count++
  if (nombreEmployes.value > 1 && accountType.value !== 'solo' && steps.value.hasStaff) count++
  return count
})

const totalSteps = computed(() => {
  // Base steps: services, horaires, photo, categories, description, subscription = 6
  // + staff if team account (nombreEmployes > 1 AND accountType !== 'solo')
  return (nombreEmployes.value > 1 && accountType.value !== 'solo') ? 7 : 6
})

const navigateTo = (path) => {
  router.push(path)
}

const completionPercentage = computed(() => {
  const percentage = Math.round((completedSteps.value / totalSteps.value) * 100)
  console.log('📊 Calcul pourcentage:', completedSteps.value, '/', totalSteps.value, '=', percentage + '%')
  return percentage
})

// Fonction pour charger/rafraîchir les données
const loadProgressData = async () => {
  try {
    // Récupérer le profil
    const profileResponse = await api.auth.getProfile()
    console.log('🔍 DEBUG ProfileCompletionProgress - Profile response:', profileResponse.data)

    // Récupérer les données de l'admin depuis le profil
    const admin = profileResponse.data?.admin || {}

    // Récupérer les données de la firme via la nouvelle route dédiée
    const firmResponse = await api.auth.getFirmInfo()
    const firm = firmResponse.data.data || firmResponse.data

    console.log('🔍 DEBUG ProfileCompletionProgress - Admin data:', admin)
    console.log('🔍 DEBUG ProfileCompletionProgress - Firm data:', firm)
    console.log('📅 DEBUG - Horaires:', firm?.horaires)

    nombreEmployes.value = firm?.nombre_employes || 1
    accountType.value = firm?.type_compte || 'solo'

    // Vérifier les services - CORRECTION: getAllServices au lieu de getAll
    const servicesResponse = await api.services.getAllServices()
    console.log(servicesResponse)
    steps.value.hasServices = servicesResponse.data?.length > 0
    console.log('✅ hasServices:', steps.value.hasServices, 'count:', servicesResponse.data.services?.length)

    // Vérifier les catégories - CORRECTION: getAllCategories au lieu de getCategories
    try {
      const categoriesResponse = await api.services.getAllCategories()
      steps.value.hasCategories = categoriesResponse.data.categories?.length > 0
      console.log('✅ hasCategories:', steps.value.hasCategories)
    } catch (catError) {
      console.log('⚠️ Erreur catégories:', catError)
      steps.value.hasCategories = false
    }

    // Vérifier les horaires (business hours)
    // Vérifier si au moins un jour a un créneau horaire valide
    if (firm?.horaires) {
      console.log('📅 Horaires existants - Type:', typeof firm.horaires)
      console.log('📅 Horaires raw:', JSON.stringify(firm.horaires, null, 2))

      // Si horaires est une string, la parser
      let horairesObj = firm.horaires
      if (typeof firm.horaires === 'string') {
        try {
          horairesObj = JSON.parse(firm.horaires)
          console.log('📅 Horaires parsé:', horairesObj)
        } catch (e) {
          console.log('❌ Erreur parsing horaires:', e)
          horairesObj = {}
        }
      }

      console.log('📅 Horaires keys:', Object.keys(horairesObj))
      console.log('📅 Horaires values:', Object.values(horairesObj))

      const hasValidSchedule = Object.values(horairesObj).some(
        (slots) => {
          console.log('  📌 Checking slot:', slots, 'isArray:', Array.isArray(slots))
          if (!Array.isArray(slots)) return false

          return slots.length > 0 && slots.some(slot => {
            console.log('    🕐 Slot details:', {
              type: typeof slot,
              debut: slot?.debut,
              fin: slot?.fin,
              hasDebut: !!slot?.debut,
              hasFin: !!slot?.fin
            })
            return slot && slot.debut && slot.fin && slot.debut.trim() !== '' && slot.fin.trim() !== ''
          })
        }
      )
      steps.value.hasSchedule = hasValidSchedule
      console.log('✅ hasSchedule FINAL:', steps.value.hasSchedule)
    } else {
      console.log('❌ Pas de horaires dans firm')
      steps.value.hasSchedule = false
    }

    // Vérifier la photo de profil de l'admin
    steps.value.hasPhoto = !!(admin?.photo && admin.photo.trim() !== '')
    console.log('✅ hasPhoto:', steps.value.hasPhoto, 'photo:', admin?.photo)

    // Vérifier la description
    steps.value.hasDescription = !!firm?.description && firm.description.length > 2
    console.log('✅ hasDescription:', steps.value.hasDescription, 'length:', firm?.description?.length)

    // Vérifier l'abonnement actif - Un vrai abonnement = stripeSubscriptionId OU type_tarification === 'lifetime'
    const hasStripeSubscription = !!(firm?.stripeSubscriptionId && firm?.subscriptionStatus === 'active')
    const hasLifetimeSubscription = firm?.type_tarification === 'lifetime'
    steps.value.hasActiveSubscription = hasStripeSubscription || hasLifetimeSubscription
    console.log('✅ hasActiveSubscription:', steps.value.hasActiveSubscription, 'stripe:', hasStripeSubscription, 'lifetime:', hasLifetimeSubscription)

    // Vérifier le staff si applicable (uniquement si pas solo)
    if (nombreEmployes.value > 1 && accountType.value !== 'solo') {
      const staffResponse = await api.staff.getAll()
      steps.value.hasStaff = staffResponse.data.admins?.length > 1
      console.log('✅ hasStaff:', steps.value.hasStaff)
    }

    console.log('📊 RÉSUMÉ - Steps complétés:', completedSteps.value, '/', totalSteps.value)
    console.log('📊 Pourcentage:', completionPercentage.value, '%')

  } catch (error) {
    console.error('❌ Erreur chargement progression profil:', error)
  }
}

// Intervalle de rafraîchissement automatique
let refreshInterval = null

onMounted(async () => {
  await loadProgressData()

  // Rafraîchir automatiquement toutes les 10 secondes
  refreshInterval = setInterval(() => {
    loadProgressData()
  }, 10000)
})

onUnmounted(() => {
  // Nettoyer l'intervalle lors de la destruction du composant
  if (refreshInterval) {
    clearInterval(refreshInterval)
  }
})

// Rafraîchir quand on revient sur le dashboard (changement de route)
watch(() => route.path, async (newPath) => {
  if (newPath === '/' || newPath === '/dashboard') {
    await loadProgressData()
  }
})

// Rafraîchir quand la fenêtre reprend le focus
if (typeof window !== 'undefined') {
  window.addEventListener('focus', loadProgressData)

  onUnmounted(() => {
    window.removeEventListener('focus', loadProgressData)
  })
}
</script>
